import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  PlayCircle,
  Save,
  Tag,
  Unlink2,
  UserPlus2,
  Webhook,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

const ORIGIN =
  typeof window !== "undefined" && window.location ? window.location.origin : "";

export default function GoogleContactsSettings() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [labelName, setLabelName] = useState("Pilates - Assessment Lead");
  const [triggerIds, setTriggerIds] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, types, l] = await Promise.all([
        api.get("/google/status", { params: { origin: ORIGIN } }),
        api.get("/appointment-types"),
        api.get("/google/log", { params: { limit: 30 } }),
      ]);
      setStatus(s.data);
      setEnabled(!!s.data.enabled);
      setLabelName(s.data.label_name || "Pilates - Assessment Lead");
      setTriggerIds(s.data.trigger_appt_type_ids || []);
      setApptTypes((types.data || []).filter((t) => !t.private));
      setLog(l.data || []);
    } catch (e) {
      // best-effort
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Surface OAuth result + clear query string
  useEffect(() => {
    if (params.get("google") === "connected") {
      toast.success("Google Contacts connected");
      setParams((p) => {
        const np = new URLSearchParams(p);
        np.delete("google");
        return np;
      });
      refresh();
    }
    const err = params.get("google_error");
    if (err) {
      toast.error("Google connect failed", { description: err });
      setParams((p) => {
        const np = new URLSearchParams(p);
        np.delete("google_error");
        return np;
      });
    }
  }, [params, setParams, refresh]);

  async function saveCreds() {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast.error("Both Client ID and Client Secret are required");
      return;
    }
    setSavingCreds(true);
    try {
      await api.post("/google/credentials", {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
      });
      toast.success("Google credentials saved");
      setClientSecret("");
      await refresh();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSavingCreds(false);
    }
  }

  async function startOAuth() {
    try {
      const r = await api.get("/google/authorize", { params: { origin: ORIGIN } });
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error("Could not start Google auth", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  // Deep-link auto-reconnect (sync-status banner "Fix" button).
  // When the URL contains ``?reconnect=google_contacts`` and we're
  // configured but disconnected, kick off the OAuth flow automatically.
  const autoReconnectFired = useRef(false);
  useEffect(() => {
    if (autoReconnectFired.current) return;
    if (params.get("reconnect") !== "google_contacts") return;
    if (!status) return;
    setParams((p) => {
      const np = new URLSearchParams(p);
      np.delete("reconnect");
      return np;
    }, { replace: true });
    const configured = !!status?.configured;
    const connected = !!status?.connected;
    if (!configured) {
      toast.info("Google Contacts credentials missing", {
        description: "Save your Client ID + Secret first, then click Connect to Google.",
      });
      return;
    }
    if (connected) {
      toast.success("Google Contacts is already connected");
      return;
    }
    autoReconnectFired.current = true;
    toast.info("Reconnecting to Google Contacts…");
    startOAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, status]);

  async function disconnect() {
    try {
      await api.delete("/google/disconnect", { params: { keep_credentials: true } });
      toast.success("Disconnected from Google");
      await refresh();
    } catch (e) {
      toast.error("Disconnect failed", { description: e.message });
    }
  }

  async function saveConfig() {
    setSavingConfig(true);
    try {
      await api.post("/google/config", {
        enabled,
        trigger_appt_type_ids: triggerIds,
        label_name: labelName.trim() || "Pilates - Assessment Lead",
      });
      toast.success("Sync settings saved");
      await refresh();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSavingConfig(false);
    }
  }

  async function registerWebhook() {
    setRegistering(true);
    try {
      const r = await api.post("/google/register-webhook", null, {
        params: { origin: ORIGIN },
      });
      toast.success("Webhook registered with Acuity", {
        description: r.data?.target_url,
      });
      await refresh();
    } catch (e) {
      toast.error("Register failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRegistering(false);
    }
  }

  // True when any control on this card differs from the saved server state.
  const hasUnsavedChanges =
    !!status &&
    (enabled !== !!status.enabled ||
      (labelName || "").trim() !==
        (status.label_name || "Pilates - Assessment Lead") ||
      JSON.stringify([...triggerIds].sort()) !==
        JSON.stringify([...(status.trigger_appt_type_ids || [])].sort()));

  async function runBatch() {
    setRunning(true);
    try {
      // Persist any pending UI changes first so the user doesn't trip over
      // the "sync is disabled" rejection when they've just toggled it on.
      if (hasUnsavedChanges) {
        await api.post("/google/config", {
          enabled,
          trigger_appt_type_ids: triggerIds,
          label_name: labelName.trim() || "Pilates - Assessment Lead",
        });
        await refresh();
      }
      const r = await api.post("/google/sync-batch?days=7", null, { timeout: 180000 });
      const { synced, matched, appointments_scanned, errors = [] } = r.data || {};
      toast.success(
        `Scanned ${appointments_scanned} appts · ${matched} matched · ${synced} synced`,
        { description: errors.length ? `${errors.length} errors` : undefined }
      );
      await refresh();
    } catch (e) {
      toast.error("Batch run failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRunning(false);
    }
  }

  function toggleTrigger(id) {
    setTriggerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const configured = !!status?.configured;
  const connected = !!status?.connected;

  return (
    <Card
      className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none"
      data-testid="google-contacts-settings"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <UserPlus2 className="h-3.5 w-3.5" /> Acuity → Google Contacts
        </div>
        <div className="text-xs text-zinc-500 font-mono-num">
          {connected ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Connected{status?.google_email ? ` · ${status.google_email}` : ""}
            </span>
          ) : configured ? (
            "Credentials saved · not connected"
          ) : (
            "Not configured"
          )}
        </div>
      </div>

      <p className="mt-3 text-sm text-zinc-600 max-w-3xl leading-relaxed">
        When a client books one of the selected appointment types, we create
        or update their Google contact with name + email + phone + a note
        ("Assessment booked DD-MM-YYYY"), and apply a Google Contacts label.
        Real-time via webhook + nightly safety-net sweep at 1:30 AM Melbourne.
      </p>

      {/* Step 1 — credentials */}
      <div className="mt-6">
        <div className="text-2xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
          Step 1 · Google Cloud OAuth credentials
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4 mb-3">
          <div className="text-xs text-zinc-500 mb-1.5">Redirect URI to whitelist</div>
          <code className="font-mono-num text-xs text-zinc-800 break-all" data-testid="google-redirect-uri">
            {status?.redirect_uri || "—"}
          </code>
          <details className="mt-3 text-xs text-zinc-600">
            <summary className="cursor-pointer hover:text-zinc-900">
              How do I get these credentials?
            </summary>
            <ol className="mt-2 list-decimal pl-4 space-y-1">
              <li>Go to <a className="underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a></li>
              <li>Create a new project (e.g. "Acuity to Google Contacts")</li>
              <li>APIs & Services → Library → enable <strong>People API</strong></li>
              <li>APIs & Services → OAuth consent screen → External → fill name + your email → keep <strong>Testing</strong> mode → add yourself under <strong>Test users</strong></li>
              <li>APIs & Services → Credentials → Create Credentials → OAuth client ID → <strong>Web application</strong></li>
              <li>Add the redirect URI shown above to <strong>Authorized redirect URIs</strong></li>
              <li>Copy the Client ID + Client Secret, paste below, click Save</li>
            </ol>
          </details>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-700">Client ID</Label>
            <Input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={configured ? "•••••••••• (saved)" : "Paste Google Client ID"}
              className="mt-1.5 font-mono-num bg-white border-zinc-300"
              data-testid="google-client-id"
            />
          </div>
          <div>
            <Label className="text-zinc-700">Client Secret</Label>
            <div className="mt-1.5 relative">
              <Input
                type={showSecret ? "text" : "password"}
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={configured ? "•••••••••• (saved)" : "Paste Google Client Secret"}
                className="font-mono-num bg-white border-zinc-300 pr-10"
                data-testid="google-client-secret"
              />
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            onClick={saveCreds}
            disabled={savingCreds}
            data-testid="google-save-creds"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {savingCreds ? "Saving…" : "Save credentials"}
          </Button>
          <Button
            onClick={startOAuth}
            disabled={!configured}
            variant="outline"
            data-testid="google-connect-btn"
            className="border-zinc-300 hover:bg-zinc-100"
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            {connected ? "Re-authorize Google" : "Connect to Google"}
          </Button>
          {connected && (
            <Button
              onClick={disconnect}
              variant="outline"
              data-testid="google-disconnect-btn"
              className="border-zinc-300 hover:bg-zinc-100"
            >
              <Unlink2 className="h-4 w-4 mr-1.5" />
              Disconnect
            </Button>
          )}
        </div>
      </div>

      {/* Step 2 — trigger types + label */}
      <div className="mt-8">
        <div className="text-2xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
          Step 2 · Trigger appointment types
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-auto thin-scroll rounded-md border border-zinc-200 p-3 bg-zinc-50"
             data-testid="google-appt-types-list">
          {apptTypes.length === 0 ? (
            <div className="text-xs text-zinc-500 col-span-full p-2">
              Loading appointment types from Acuity…
            </div>
          ) : (
            apptTypes.map((t) => (
              <label
                key={t.id}
                className="flex items-start gap-2 text-sm bg-white border border-zinc-200 rounded px-2 py-1.5 hover:border-zinc-400 cursor-pointer"
              >
                <Checkbox
                  checked={triggerIds.includes(Number(t.id))}
                  onCheckedChange={() => toggleTrigger(Number(t.id))}
                  data-testid={`google-trigger-${t.id}`}
                  className="mt-0.5"
                />
                <span className="text-zinc-800 leading-snug">{t.name}</span>
              </label>
            ))
          )}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <div>
            <Label className="text-zinc-700 inline-flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" /> Google Contacts label
            </Label>
            <Input
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              className="mt-1.5 bg-white border-zinc-300"
              data-testid="google-label-name"
            />
            <p className="mt-1.5 text-xs text-zinc-500">
              We'll create this label in Google Contacts if it doesn't exist.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2.5">
            <div className="text-sm">
              <div className="font-medium text-zinc-900">Enable sync</div>
              <div className="text-2xs text-zinc-500">
                Without this, webhooks are received but no contact is written.
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="google-enabled-switch"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            onClick={saveConfig}
            disabled={savingConfig}
            data-testid="google-save-config"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {savingConfig ? "Saving…" : "Save sync settings"}
          </Button>
          {hasUnsavedChanges && (
            <span
              data-testid="google-unsaved-badge"
              className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-900"
              title="Click Save sync settings to persist your changes"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
          )}
          <Button
            onClick={registerWebhook}
            disabled={registering || !connected}
            variant="outline"
            data-testid="google-register-webhook"
            className="border-zinc-300 hover:bg-zinc-100"
          >
            <Webhook className="h-4 w-4 mr-1.5" />
            {registering ? "Registering…" : "Register webhook in Acuity"}
          </Button>
          <Button
            onClick={runBatch}
            disabled={running || !connected || (!enabled && !hasUnsavedChanges)}
            variant="outline"
            data-testid="google-run-batch"
            className="border-zinc-300 hover:bg-zinc-100"
            title={
              hasUnsavedChanges
                ? "Will save your changes first, then run the sweep"
                : ""
            }
          >
            <PlayCircle className="h-4 w-4 mr-1.5" />
            {running ? "Running…" : "Run 7-day sweep now"}
          </Button>
        </div>
      </div>

      {/* Recent activity */}
      {log.length > 0 && (
        <div className="mt-8" data-testid="google-log">
          <div className="text-2xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
            Recent contact syncs
          </div>
          <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
            <ul className="divide-y divide-zinc-100">
              {log.slice(0, 10).map((row) => (
                <li
                  key={row.appointment_id}
                  className="px-3 py-2 flex items-center justify-between gap-3 text-sm"
                  data-testid={`google-log-row-${row.appointment_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-zinc-900 truncate">
                      {`${row.snapshot?.firstName || ""} ${row.snapshot?.lastName || ""}`.trim()
                        || row.snapshot?.email
                        || `Appt ${row.appointment_id}`}
                    </div>
                    <div className="text-2xs text-zinc-500 truncate">
                      {row.snapshot?.type}
                      {row.snapshot?.datetime ? ` · ${row.snapshot.datetime.slice(0, 16).replace("T", " ")}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {row.status === "ok" ? (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {row.action}
                      </span>
                    ) : (
                      <span
                        className="text-xs text-rose-700"
                        title={row.error || ""}
                      >
                        {row.action} · failed
                      </span>
                    )}
                    <div className="text-2xs text-zinc-400 font-mono-num">
                      {row.updated_at_iso ? fmtDateTime(row.updated_at_iso) : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
}
